import zipCbsaData from "../data/zip-cbsa.json";
import {
  CBSA_MULTIPLES,
  STATE_MULTIPLES,
  NATIONAL_AVERAGE,
} from "../data/geo-multiples";
import type {
  MarketOverrides,
  BlendingDetails,
  ConfidenceScore,
} from "./types";
interface ZipLookup {
  cbsa: string;
  state: string;
}

const zipData = zipCbsaData as Record<string, ZipLookup>;

function lookupZip(zipCode: string): ZipLookup | undefined {
  const padded = zipCode.padStart(5, "0");
  return zipData[padded];
}

export function getGeoAdjustment(zipCode: string | undefined): {
  revenueAdj: number;
  cashflowAdj: number;
  cbsa: string | null;
  state: string | null;
  geoLevel: "cbsa" | "state" | "national";
} {
  if (!zipCode) {
    return {
      revenueAdj: 0,
      cashflowAdj: 0,
      cbsa: null,
      state: null,
      geoLevel: "national",
    };
  }

  const zipInfo = lookupZip(zipCode);
  if (!zipInfo) {
    return {
      revenueAdj: 0,
      cashflowAdj: 0,
      cbsa: null,
      state: null,
      geoLevel: "national",
    };
  }

  const cbsaName = zipInfo.cbsa;
  const stateName = zipInfo.state;

  if (cbsaName) {
    const cbsaEntry = CBSA_MULTIPLES.find((e) => e.market === cbsaName);
    if (cbsaEntry) {
      const revAdj =
        cbsaEntry.revenueMultiple / NATIONAL_AVERAGE.revenueMultiple - 1;
      const cfAdj =
        cbsaEntry.cashflowMultiple / NATIONAL_AVERAGE.cashflowMultiple - 1;
      return {
        revenueAdj: revAdj,
        cashflowAdj: cfAdj,
        cbsa: cbsaName,
        state: stateName,
        geoLevel: "cbsa",
      };
    }
  }

  if (stateName) {
    const stateEntry = STATE_MULTIPLES.find((e) => e.state === stateName);
    if (stateEntry) {
      const revAdj =
        stateEntry.revenueMultiple / NATIONAL_AVERAGE.revenueMultiple - 1;
      const cfAdj =
        stateEntry.cashflowMultiple / NATIONAL_AVERAGE.cashflowMultiple - 1;
      return {
        revenueAdj: revAdj,
        cashflowAdj: cfAdj,
        cbsa: cbsaName || null,
        state: stateName,
        geoLevel: "state",
      };
    }
  }

  return {
    revenueAdj: 0,
    cashflowAdj: 0,
    cbsa: cbsaName || null,
    state: stateName || null,
    geoLevel: "national",
  };
}

/**
 * Blend BizBuySell multiples with Venturu's own platform data,
 * weighted proportionally by the number of transactions in each source.
 */
export function blendMultiples(
  bizBuySellRevMult: number,
  bizBuySellCfMult: number,
  bizBuySellReportedSales: number,
  overrides: MarketOverrides,
): { revMult: number; cfMult: number; blending: BlendingDetails } {
  const totalWeight = bizBuySellReportedSales + overrides.transactionCount;
  const bizWeight = bizBuySellReportedSales / totalWeight;
  const venturuWeight = overrides.transactionCount / totalWeight;

  const revMult =
    bizBuySellRevMult * bizWeight + overrides.revenueMultiple * venturuWeight;
  const cfMult =
    bizBuySellCfMult * bizWeight + overrides.sdeMultiple * venturuWeight;

  return {
    revMult,
    cfMult,
    blending: {
      bizBuySellWeight: Math.round(bizWeight * 1000) / 1000,
      venturuWeight: Math.round(venturuWeight * 1000) / 1000,
      venturuTransactionCount: overrides.transactionCount,
      venturuRevenueMultiple: overrides.revenueMultiple,
      venturuSdeMultiple: overrides.sdeMultiple,
      bizBuySellRevenueMultiple: bizBuySellRevMult,
      bizBuySellCashflowMultiple: bizBuySellCfMult,
    },
  };
}

export function computeConfidence(
  sectorReportedSales: number,
  geoLevel: "cbsa" | "state" | "national",
  revenueImpliedPrice: number,
  cashflowImpliedPrice: number,
  revenueHistoryLength: number,
  hasVenturuData: boolean,
): ConfidenceScore {
  const sectorDataDepth = Math.min(sectorReportedSales / 50, 1);

  const geoCoverage =
    geoLevel === "cbsa" ? 1.0 : geoLevel === "state" ? 0.7 : 0.4;

  const higher = Math.max(revenueImpliedPrice, cashflowImpliedPrice);
  const lower = Math.min(revenueImpliedPrice, cashflowImpliedPrice);
  const spread = higher > 0 ? (higher - lower) / higher : 0;
  const valuationAgreement = Math.max(1 - spread * 2, 0);

  const revenueDataDepth = Math.min(revenueHistoryLength / 3, 1);

  const venturuDataAvailable = hasVenturuData ? 1.0 : 0;

  const overall =
    sectorDataDepth * 0.25 +
    geoCoverage * 0.2 +
    valuationAgreement * 0.25 +
    revenueDataDepth * 0.15 +
    venturuDataAvailable * 0.15;

  return {
    overall: Math.round(overall * 100) / 100,
    factors: {
      sectorDataDepth: Math.round(sectorDataDepth * 100) / 100,
      geoCoverage: Math.round(geoCoverage * 100) / 100,
      valuationAgreement: Math.round(valuationAgreement * 100) / 100,
      revenueDataDepth: Math.round(revenueDataDepth * 100) / 100,
      venturuDataAvailable: Math.round(venturuDataAvailable * 100) / 100,
    },
  };
}
